import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@folio/components';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import {
  claimsListQueryOptions,
  getClaimsCategoryLabel,
  groupClaimsByCategory,
} from '@folio/utils/claims';
import { keepPreviousData, useQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  type AssetDto,
  type ClaimDto,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { AssetClaimsCategory } from './AssetClaimsCategory';
import { Center, Stack } from '@folio/components/ui/flex';

type AssetClaimsModuleProps = {
  claimsMetadata?: Record<string, string>;
};

export function AssetClaimsModule({ claimsMetadata }: AssetClaimsModuleProps) {
  const { t } = useTranslation();
  const { korintConfig } = useKorintConfig();
  const assetClaimsConfig = korintConfig.modulesConfig?.assetClaims;
  const claimsConfig = usePolicyProductConfig('claims');
  const { policy, workingBranch } = usePolicyOnDashboard();

  const { data: assetsData } = useSuspenseQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
  });

  const assets = assetsData?.data || [];
  const assetIds = assets.map((asset: AssetDto) => asset.id);

  const assetClaimsQueries = useQueries({
    queries: assetIds.map((assetId: string) => ({
      ...claimsListQueryOptions(assetId, assetClaimsConfig?.claimsMetadata),
      placeholderData: keepPreviousData,
    })),
  });

  const allClaims = assetClaimsQueries.flatMap(
    (query: { data?: { data?: ClaimDto[] } }) => query.data?.data || [],
  ) as ClaimDto[];

  const isLoading = assetClaimsQueries.some((query: { isLoading: boolean }) => query.isLoading);
  const isError = assetClaimsQueries.some((query: { isError: boolean }) => query.isError);

  const refetchClaims = async (): Promise<{ data: ClaimDto[] }> => {
    const results = await Promise.all(assetClaimsQueries.map(query => query.refetch()));
    const freshClaims = results.flatMap(result => (result.data?.data || []) as ClaimDto[]);
    return {
      data: freshClaims,
    };
  };

  const assetsById = Object.fromEntries(assets.map(asset => [asset.id, asset]));

  return (
    <Card>
      <CardHeader className="flex flex-row justify-between items-start space-y-0">
        <CardTitle>{t('assetClaimsModule.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Stack gap={1}>
          {isLoading && (
            <Stack gap={2}>
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
              <Skeleton className="h-[40px] w-full" />
            </Stack>
          )}
          {isError && <Center className="h-full">{t('assetClaims.fetchError')}</Center>}
          {!isLoading &&
            !isError &&
            groupClaimsByCategory(allClaims, claimsConfig.claimsCategories).map(group => (
              <AssetClaimsCategory
                key={group.category}
                assetsById={assetsById}
                category={group.category}
                label={getClaimsCategoryLabel(policy.productId, group, t)}
                hasResponsibility={group.hasResponsibility}
                claimsMetadata={claimsMetadata || assetClaimsConfig?.claimsMetadata}
                categoryClaims={group.claims}
                refetchClaims={refetchClaims}
                fieldsConfig={claimsConfig.fieldsConfig}
              />
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
